import { useNavigate } from "react-router-dom";
import { PencilLine } from "lucide-react";
import { getSystemIconPath } from "../../../../lib/systemOfRecord";
import {
  WORK_STEP_OPTRO_IMPORT_PATH,
  useOptroImportLauncher,
} from "../../../../hooks/useOptroImportLauncher";
import { OptroConnectDialog } from "../../controls/optroImport/components/OptroConnectDialog";

interface WorkStepCreationOptionsProps {
  title: string;
  subtitle: string;
}

// The ways to get work steps into Midship: bulk import from Optro's opsaudit
// module (wayfinder ticket 08), or the manual/AI creation form. Mirrors
// ControlCreationOptions — the Optro card is gated on the workspace's Optro
// connection via useOptroImportLauncher, opening the same "Configure
// Optro" dialog the controls chooser uses.
export const WorkStepCreationOptions = ({
  title,
  subtitle,
}: WorkStepCreationOptionsProps) => {
  const navigate = useNavigate();
  const { launchImport, dialogProps } = useOptroImportLauncher({
    wizardPath: WORK_STEP_OPTRO_IMPORT_PATH,
  });
  return (
    <div className="py-12 max-w-2xl mx-auto text-left">
      <h2 className="text-2xl font-semibold text-center text-stone-900">
        {title}
      </h2>
      <p className="text-sm text-stone-500 text-center mt-2 mb-8">{subtitle}</p>

      <button
        type="button"
        onClick={launchImport}
        className="w-full text-left border rounded-xl p-5 mb-4 hover:bg-stone-50 transition"
      >
        <div className="flex items-start gap-4">
          <div className="h-10 w-10 bg-stone-100 rounded-lg flex items-center justify-center">
            <img
              src={getSystemIconPath("auditboard")}
              alt=""
              className="h-5 w-5"
            />
          </div>
          <div>
            <div className="text-base font-semibold text-stone-900">
              Import work steps from Optro
            </div>
            <div className="text-sm text-stone-600">
              Connect Optro and bulk import work steps from your OpsAudits. Each
              work step keeps its audit, and missing audits are created
              automatically.
            </div>
          </div>
        </div>
      </button>

      <button
        type="button"
        onClick={() => navigate("/audit/work-steps/create/manual")}
        className="w-full text-left border rounded-xl p-5 hover:bg-stone-50 transition"
      >
        <div className="flex items-start gap-4">
          <div className="h-10 w-10 bg-stone-100 rounded-lg flex items-center justify-center">
            <PencilLine size={20} />
          </div>
          <div>
            <div className="text-base font-semibold text-stone-900">
              Import work steps manually
            </div>
            <div className="text-sm text-stone-600">
              Generate a work step from your documentation with AI, bulk import
              from a spreadsheet, or build one from scratch.
            </div>
          </div>
        </div>
      </button>

      <OptroConnectDialog {...dialogProps} />
    </div>
  );
};
